import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { isMobile, isTablet } from 'react-device-detect';
import { toast } from 'react-toastify';

import {
  trackOnboardingModalClose,
  trackOnboardingModalVideoWatchFully,
  trackOnboardingModalVideoWatchPartially,
  trackOnboardingModalClickRemindMeLater,
  trackQuickstartPackDownload,
} from '../../../../analytics';

import {
  Button,
  ButtonLink,
  Modal,
  Logo,
} from '../../../../components/general';

import { HELP_VIDEO_ID, ZIP_URL } from '../../../../constants';

declare global {
  interface Window {
    onYouTubeIframeAPIReady?: () => void;
    YT: typeof YT;
  }

  namespace YT {
    class Player {
      constructor(elementId: string, options: PlayerOptions);
      playVideo(): void;
      pauseVideo(): void;
      stopVideo(): void;
    }

    interface PlayerOptions {
      height: string;
      width: string;
      videoId: string;
      events?: {
        onStateChange?: (event: OnStateChangeEvent) => void;
      };
      enablejsapi: number;
    }

    interface PlayerEvent {
      target: Player;
    }

    interface OnStateChangeEvent extends PlayerEvent {
      data: number;
    }

    enum PlayerState {
      UNSTARTED = -1,
      ENDED = 0,
      PLAYING = 1,
      PAUSED = 2,
      BUFFERING = 3,
      CUED = 5,
    }
  }
}

const OnboardingVideoModal = (props: {
  isNewUser: boolean;
  showOnboardingVideo: boolean;
  setShowOnboardingVideo: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const { isNewUser, showOnboardingVideo, setShowOnboardingVideo } = props;

  const playerRef = useRef<YT.Player | null>(null);
  const [watched, setWatched] = useState(false);
  const [watchedPartially, setWatchedPartially] = useState(false);

  useEffect(() => {
    const lastSeen = localStorage.getItem('lastSeenIntroModal');

    if (isNewUser) {
      if (!lastSeen) {
        // If the user has never seen the modal, show it
        setShowOnboardingVideo(true);
      } else {
        const lastSeenDate = new Date(lastSeen);
        const currentDate = new Date();

        // Calculate the difference in days
        const diffInDays = Math.round(
          (currentDate.getTime() - lastSeenDate.getTime()) /
            (1000 * 60 * 60 * 24),
        );

        if (diffInDays >= 28) {
          // If a month or more has passed since the user last saw the modal, show it again
          setShowOnboardingVideo(true);
        }
      }
    }
  }, [isNewUser]);

  const handleClose = () => {
    setShowOnboardingVideo(false);
    trackOnboardingModalClose();

    handlePlayerGATracking();

    const showToastAfterTheModal = localStorage.getItem(
      'showToastAfterTheModal',
    );
    if (isNewUser && showToastAfterTheModal) {
      setTimeout(() => {
        if (isMobile || isTablet) {
          localStorage.removeItem('showToastAfterTheModal');
          toast.warning(
            <div>
              <div>CrowdClip&reg; is best used on a desktop.</div>
              <div>
                You can continue on your mobile device, but beware, things will
                look a bit funny.
              </div>
            </div>,
            {
              autoClose: false,
            },
          );
        }
      }, 200);
    }
  };

  const handleDownloadQuickStart = () => {
    // Update the date when the user last saw the modal
    localStorage.setItem('lastSeenIntroModal', new Date().toISOString());

    trackQuickstartPackDownload('Onboarding modal');
  };

  const handleCloseRemindLater = () => {
    setShowOnboardingVideo(false);
    trackOnboardingModalClickRemindMeLater();

    handlePlayerGATracking();

    const showToastAfterTheModal = localStorage.getItem(
      'showToastAfterTheModal',
    );
    if (isNewUser && showToastAfterTheModal) {
      setTimeout(() => {
        if (isMobile || isTablet) {
          localStorage.removeItem('showToastAfterTheModal');
          toast.warning(
            <div>
              <div>CrowdClip&reg; is best used on a desktop.</div>
              <div>
                You can continue on your mobile device, but beware, things will
                look a bit funny.
              </div>
            </div>,
            {
              autoClose: false,
            },
          );
        }
      }, 200);
    }
  };

  const handlePlayerGATracking = () => {
    if (watched && !watchedPartially) trackOnboardingModalVideoWatchFully();
    else if (!watched && watchedPartially)
      trackOnboardingModalVideoWatchPartially();
  };

  useEffect(() => {
    if (isNewUser || showOnboardingVideo) {
      playerRef.current = new window.YT.Player('player', {
        height: '100%',
        width: '100%',
        videoId:
          isMobile || isTablet
            ? HELP_VIDEO_ID.ONBOARDING_MOBILE
            : HELP_VIDEO_ID.ONBOARDING_DESKTOP,

        events: {
          onStateChange: onPlayerStateChange,
        },
        enablejsapi: 1,
      });
    }
  }, [isNewUser, showOnboardingVideo]);

  const onPlayerStateChange = (event: YT.OnStateChangeEvent) => {
    if (event.data === YT.PlayerState.PLAYING) {
      setWatchedPartially(true);
    } else if (event.data === YT.PlayerState.PAUSED) {
      setWatchedPartially(true);
    } else if (event.data === YT.PlayerState.ENDED) {
      setWatched(true);
      setWatchedPartially(false);
    }
  };

  const videoClass = classNames({
    'w-full rounded-md': true,
    'aspect-[9/16] max-h-[90vh]': isMobile || isTablet,
    'aspect-video': !isMobile && !isTablet,
  });

  return (
    <Modal
      show={showOnboardingVideo}
      onHide={handleClose}
      size="lg"
      className="text-white"
    >
      <Modal.Body className="px-4 md:px-12 md:py-8 sm:gap-7 text-center bg-cc-purple-700 rounded-md">
        <div className="text-xl sm:text-2xl font-medium leading-10">
          Welcome to{' '}
          <Logo
            variant="white"
            width="140px"
            className="align-middle inline-block"
          />
        </div>

        <div className="text-lg sm:text-xl">
          <b>Before you begin!</b> Learn how CrowdClip&reg;'s AI can help you
          generate hundreds of videos for your clients events in a few clicks
        </div>

        <div id="player" className={videoClass} />

        <div className="flex flex-col items-center gap-4">
          {isMobile || isTablet ? (
            <div>
              We recommend downloading Quickstart pack on your desktop computer.
              The Quickstart pack is a ZIP, that contains pre-made media assets,
              to help you get started.
            </div>
          ) : (
            <div className="text-lg sm:text-xl">
              Get a head start, download ready-to-try assets and give
              CrowdClip&reg; a go!
            </div>
          )}

          <ButtonLink
            to={ZIP_URL.QUICKSTART}
            onClick={() => handleDownloadQuickStart()}
            variant="pill"
            colour="black"
            className="px-6 py-3 font-medium border-white border-2"
          >
            Download the quickstart pack
          </ButtonLink>

          <Button
            onClick={handleCloseRemindLater}
            variant="link-no-underline"
            colour="white"
          >
            Remind me later
          </Button>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default OnboardingVideoModal;
